import {Inject, Injectable} from "@angular/core";
import * as XLSX from "xlsx";

import {
    AccionesItemDTO,
    ComboMovilDTO,
    EncuestaCargaDTO,
    EncuestaDTO,
    EncuestaVaciaDTO,
    ImagenURLDTO,
    ItemPreguntaDTO,
    PaisDTO,
    PreguntaDTO,
    PruebaInternaDTO,
    TemplateDTO
} from "@DTOS";
import {EncuestasServiceBackend} from "@serviceBackends";
import {ModalService, NotificacionServiceEmisor} from "@shared/services";
import {Nilable} from "@shared/utils";
import {FilasExcel, obtenerColumnaArray, obtenerColumnaMap, obtenerColumnaNuevoArray, obtenerColumnas, PreguntasTipoEnum} from "@shared/enums";
import {DatosAnteriores, DatosPreguntaImportador, PreguntaImportador} from "@shared/clases/importador";
import {Constants} from "@constants";
import {firstValueFrom, Observable, tap} from "rxjs";
import {GoogleApiKeyToken} from "@shared/tokens";
import {PreguntaImportadorToken} from "@shared/tokens/PreguntaImportadorToken";
import { ConfiguracionCrearCuestionarioExcel, ConfiguracionCrearCuestionarioSpreadsheet } from "./interfaces/ConfiguracionCrearCuestionario";
import { EncuestaCargaDatos } from "./interfaces/EncuestaCargaDatos";

@Injectable({
    providedIn: "root"
})
export class EncuestasService {
    public cantidad = 0;
    public ultimaPagina;
    private encuestas: EncuestaDTO[];
    private queryAnterior: string;
    private encuestasBuscadas: Nilable<EncuestaDTO[]>;

    constructor(private encuestasServiceBackend: EncuestasServiceBackend, private modalService: ModalService,
                private notificacionService: NotificacionServiceEmisor,
                @Inject(PreguntaImportadorToken) private importadoresPreguntas: PreguntaImportador[],
                @Inject(GoogleApiKeyToken) public googleApiKey: string) {
    }

    public async obtenerEncuestas(pagina: number, cantidad: number): Promise<EncuestaDTO[]> {
        this.encuestasBuscadas = null;

        if (this.encuestas == null) {
            const paginadoEncuestas = await this.encuestasServiceBackend.getEncuestas(pagina, cantidad);

            this.encuestas = paginadoEncuestas.resultados;
            this.ultimaPagina = paginadoEncuestas.ultimaPagina;
            this.cantidad = paginadoEncuestas.cantidad;
        }

        if (pagina !== 1) {
            const encuestasNuevas = await this.encuestasServiceBackend.getEncuestas(pagina, cantidad);
            this.ultimaPagina = encuestasNuevas.ultimaPagina;
            this.encuestas = this.encuestas.concat(encuestasNuevas.resultados);
        }

        return [...this.encuestas];
    }

    public async obtenerEncuestasQuery(query: string, pagina: number, cantidad: number): Promise<EncuestaDTO[]> {
        if (this.queryAnterior == null || query !== this.queryAnterior) {
            this.queryAnterior = query;
            this.encuestasBuscadas = [];
        }

        const encuestasEncontradas = await this.encuestasServiceBackend.getEncuestasQuery(query, pagina, cantidad);

        if (query === this.queryAnterior) {
            this.ultimaPagina = encuestasEncontradas.ultimaPagina;
            this.encuestasBuscadas = this.encuestasBuscadas?.concat(encuestasEncontradas.resultados) ?? [];
        }

        return this.encuestasBuscadas ?? [];
    }

    public async subirCuestionario(encuesta: EncuestaCargaDTO): Promise<Nilable<EncuestaDTO>> {
        const encuestaNueva = await this.encuestasServiceBackend.postEncuestaRaw(encuesta);

        if (encuestaNueva == null) {
            return null;
        }

        if (this.tienePostAcciones(encuesta)) {
            const preguntas = await this.obtenerPreguntas(encuestaNueva);
            await this.cargarFotosGraphicalChoice(encuesta, encuestaNueva, preguntas);
        }

        if (this.encuestas != null) {
            this.encuestas.unshift(encuestaNueva);
        }

        return encuestaNueva;
    }

    public async agregarEncuesta(encuesta: EncuestaVaciaDTO): Promise<EncuestaDTO> {
        const encuestaNuevo = await this.encuestasServiceBackend.postEncuesta(encuesta);

        if (this.encuestas != null) {
            this.encuestas.unshift(encuestaNuevo);
        }

        return encuestaNuevo;
    }

    public async obtenerEncuestaPublica(id: string): Promise<EncuestaDTO> {
        const encuesta = await this.encuestasServiceBackend.getEncuestaPublica(id);
        this.asignarIdRelativo(encuesta);
        return encuesta;
    }

    public async obtenerEncuesta(id: number): Promise<EncuestaDTO> {
        if (this.encuestas) {
            const encuestaEncontrada = this.encuestas.find(encuesta => encuesta.id === id);

            if (encuestaEncontrada) {
                return encuestaEncontrada;
            }
        }

        return await this.encuestasServiceBackend.getEncuesta(id);
    }

    public async guardarEncuesta(encuesta: EncuestaDTO): Promise<EncuestaDTO> {
        const encuestaNueva = await this.encuestasServiceBackend.putEncuesta(encuesta);

        if (this.encuestas != null) {
            this.encuestas = this.encuestas.map(encuestaDTO =>
                encuestaDTO.id === encuestaNueva.id ? encuestaNueva : encuestaDTO);
        }

        return encuestaNueva;
    }

    public async borrarEncuesta(encuesta: EncuestaDTO): Promise<void> {
        if (this.encuestas) {
            this.encuestas = this.encuestas.filter(encuestaDTO => encuestaDTO.id !== encuesta.id);
        }

        await this.encuestasServiceBackend.deleteEncuesta(encuesta);
    }

    public getPreguntaById(encuesta: EncuestaDTO, id: number): PreguntaDTO {
        return encuesta.preguntas?.find(question => question.id === id) ?? new PreguntaDTO();
    }

    public async getPreguntasAfinidad(project: number): Promise<PreguntaDTO[]> {
        return this.encuestasServiceBackend.getPreguntasAfinidad(project);
    }

    public async obtenerPreguntas(encuesta: EncuestaDTO): Promise<PreguntaDTO[]> {
        let preguntas: PreguntaDTO[] = await this.encuestasServiceBackend.getPreguntasEncuesta(encuesta);
        encuesta.preguntas = preguntas;
        this.asignarIdRelativo(encuesta);
        preguntas = preguntas.filter(pregunta => !pregunta.padre);
        return preguntas;

    }

    public async agregarPregunta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<PreguntaDTO> {
        pregunta.encuesta = encuesta.id;
        let subpreguntas = pregunta.preguntas ?? [];
        pregunta.preguntas = null;
        let preguntaDTO = await this.encuestasServiceBackend.postPreguntaEncuesta(encuesta, pregunta);
        if (pregunta.tipo === PreguntasTipoEnum.FLOW_GROUP) {
            preguntaDTO.idBloque = preguntaDTO.id;
            preguntaDTO = await this.encuestasServiceBackend.putPreguntaEncuesta(encuesta, preguntaDTO);
            subpreguntas = await Promise.all(subpreguntas.map(async subPregunta => {
                subPregunta.idBloque = preguntaDTO.id;
                return await this.encuestasServiceBackend.postPreguntaEncuesta(encuesta, subPregunta);
            }));
        }
        if (encuesta.preguntas != null && encuesta.preguntas.length > 0) {
            const ultimaPregunta = encuesta.preguntas[encuesta.preguntas.length - 1];
            ultimaPregunta.siguientePregunta = preguntaDTO.id;
            await this.guardarPregunta(encuesta, ultimaPregunta);
        }
        encuesta.preguntas?.push(preguntaDTO);
        this.asignarIdRelativo(encuesta);
        encuesta.preguntas?.push(...subpreguntas);
        return preguntaDTO;
    }

    public async guardarPregunta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<PreguntaDTO> {
        const preguntaModificada = await this.encuestasServiceBackend.putPreguntaEncuesta(encuesta, pregunta);
        encuesta.preguntas = encuesta.preguntas?.map(preguntaDTO =>
            preguntaDTO.id === preguntaModificada.id ? preguntaModificada : preguntaDTO) ?? [];
        return preguntaModificada;
    }

    public async borrarPregunta(encuesta: EncuestaDTO, pregunta: PreguntaDTO): Promise<void> {
        encuesta.preguntas = encuesta.preguntas!.filter(preguntaDTO => preguntaDTO.id !== pregunta.id);
        this.asignarIdRelativo(encuesta);
        await this.encuestasServiceBackend.deletePreguntaEncuesta(encuesta, pregunta);
    }

    public async agregarSubpreguntaPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, subpregunta: PreguntaDTO
    ): Promise<PreguntaDTO> {
        subpregunta.padre = pregunta.id;

        const subpreguntaDTO = await this.encuestasServiceBackend.postPreguntaEncuesta(encuesta, subpregunta);

        pregunta.preguntas?.push(subpreguntaDTO);
        this.asignarIdRelativo(encuesta);
        return subpreguntaDTO;
    }

    public async guardarSubpreguntaPregunta(encuesta: EncuestaDTO, subpregunta: PreguntaDTO): Promise<PreguntaDTO> {
        return await this.encuestasServiceBackend.putPreguntaEncuesta(encuesta, subpregunta);
    }

    public async borrarSubpreguntaPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, subpregunta: PreguntaDTO
    ) {
        await this.encuestasServiceBackend.deletePreguntaEncuesta(encuesta, subpregunta);
        encuesta.preguntas = encuesta.preguntas?.filter(subpreguntaDTO => subpreguntaDTO.id !== subpregunta.id) ?? [];
        pregunta.preguntas = pregunta.preguntas?.filter(subpreguntaDTO => subpreguntaDTO.id !== subpregunta.id) ?? [];
        this.asignarIdRelativo(encuesta);
    }

    public async agregarItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<ItemPreguntaDTO> {
        const itemDTO = await this.encuestasServiceBackend.postItemPregunta(encuesta, pregunta, item);

        itemDTO.idRelativo = `${pregunta.idRelativo}.${pregunta.items.length + 1}`;
        pregunta.items.push(itemDTO);

        return itemDTO;
    }

    public async guardarItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<ItemPreguntaDTO> {
        return await this.encuestasServiceBackend.putItemPregunta(encuesta, pregunta, item);
    }

    public async borrarItemPregunta(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO
    ): Promise<void> {
        await this.encuestasServiceBackend.deleteItemPregunta(encuesta, pregunta, item);
        pregunta.items = pregunta.items.filter(itemDTO => itemDTO.id !== item.id);
        this.asignarIdRelativo(encuesta);
    }

    public async subirFoto(encuesta: EncuestaDTO, pregunta: PreguntaDTO, data: FormData): Promise<PreguntaDTO> {
        return await this.encuestasServiceBackend.patchSubirFotoPregunta(encuesta, pregunta, data);
    }

    public async borrarFoto(imageUrl: string): Promise<void> {
        await this.encuestasServiceBackend.deleteFotoPregunta(imageUrl);
    }

    public async comboMovil(comboMovil: ComboMovilDTO): Promise<void> {
        await this.encuestasServiceBackend.postComboMovil(comboMovil);
    }

    public async pruebaInterna(pruebaInterna: PruebaInternaDTO): Promise<void> {
        await this.encuestasServiceBackend.postPruebaInterna(pruebaInterna);
    }

    public async importarCombo(
        archivo: any, paises: PaisDTO[], investigacion_id: number
    ): Promise<Nilable<EncuestaDTO>> {
        return new Promise(resolve => {
            if (!archivo) {
                resolve(null);
            }

            const reader = new FileReader();

            reader.onload = async (e: any) => {
                const encuesta = await this.procesarExcel(archivo.name, e.target.result, paises, investigacion_id);
                resolve(encuesta);
            };
            reader.readAsBinaryString(archivo);
        });
    }

    public obtenerTodosTemplates(): Observable<TemplateDTO[]> {
        return this.encuestasServiceBackend.getTemplates();
    }

    public async generarEncuestaCargaExcel(
        configuracion: ConfiguracionCrearCuestionarioExcel
    ): Promise<Nilable<EncuestaCargaDatos>> {
        return new Promise(resolve => {
            if (configuracion.archivo == null) {
                resolve(null);
            }

            const reader = new FileReader();

            reader.onload = async (e: any) => {
                if (configuracion.paises == null) {
                    resolve(null);
                }
                const encuesta = await this.procesarExcel(configuracion.archivo.name, e.target.result, configuracion.paises ?? [], 0);
                const encuestaCargaDatos = this.generarEncuestaCarga(e.result, configuracion.paises!);
                resolve(encuestaCargaDatos);
            };
            reader.readAsBinaryString(configuracion.archivo);
        });
    }

    public async generarEncuestaCargaSpreadsheet(
        configuracion: ConfiguracionCrearCuestionarioSpreadsheet
    ): Promise<Nilable<EncuestaCargaDatos>> {
        try {
            const response = await gapi.client.sheets.spreadsheets.values.get({
                spreadsheetId: configuracion.id,
                range: `${configuracion.hoja}!D2:O${configuracion.cantidadLineas}`
            });
            const result = response.result;
            if (result.values == null) {
                this.notificacionService.notificarError("El cuestionario no tiene preguntas");
                return null;
            }
            if (configuracion.paises == null) {
                this.notificacionService.notificarError("No se pudieron cargar los paises");
                return null;
            }
            const encuestaCargaDatos = this.generarEncuestaCarga(result.values, configuracion.paises, false);
            const { encuesta } = encuestaCargaDatos
            if (encuesta == null) {
                return null;
            }
            encuesta.primeraPregunta = encuesta.preguntas.length > 0 ? encuesta.preguntas[0].id : "1";
            encuesta.investigacionId = 0;
            return encuestaCargaDatos;
        } catch (ex) {
            return null
        }
    }

    public async leerGoogleSpreadSheet(
        spreadsheetId: string, cantidadFilas: string, paises: PaisDTO[], nombre: string
    ): Promise<Nilable<EncuestaDTO>> {
        await new Promise(resolve => {
            gapi.load("client", resolve);
        });
        await gapi.client.init({
            apiKey: this.googleApiKey,
            discoveryDocs: ["https://sheets.googleapis.com/$discovery/rest?version=v4"]
        });

        const hojasRequest = await gapi.client.sheets.spreadsheets.get({
            spreadsheetId,
            includeGridData: false,
            fields: "sheets.properties.title"
        });
        const hojas = hojasRequest.result.sheets?.map(hoja => hoja.properties?.title ?? "")
            .filter(tituloHoja => tituloHoja !== "") ?? [];
        const opcion = await this.modalService.openModalInputOpciones({
            items: hojas,
            item: hojas[0],
            clear: false,
            label: "",
            multiple: false
        }, {
            centered: true
        });
        if (opcion == null) {
            return null;
        }
        const response = await gapi.client.sheets.spreadsheets.values.get({
            spreadsheetId,
            range: `${opcion}!D2:O${cantidadFilas}`
        });
        const result = response.result;
        if (result.values == null) {
            this.notificacionService.notificarError("El cuestionario no tiene preguntas");
            return null;
        }
        const encuesta = await this.filasAEncuesta(result.values, paises, false);
        if (encuesta == null) {
            return null;
        }
        encuesta.nombre = nombre;
        encuesta.primeraPregunta = encuesta.preguntas.length > 0 ? encuesta.preguntas[0].id : "1";
        encuesta.investigacionId = 0;
        return await this.subirCuestionario(encuesta);
    }

    public agregarAccionItem(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO, accion: AccionesItemDTO
    ): Observable<AccionesItemDTO> {
        accion.item = item.id;
        return this.encuestasServiceBackend.postAccionItem(encuesta.id, pregunta.id, item.id, accion);
    }

    public obtenerAccionesItem(encuesta: EncuestaDTO): Observable<AccionesItemDTO[]> {
        return this.encuestasServiceBackend.getAccionesItem(encuesta.id).pipe(
            tap(acciones => this.cargarAccionesEnEncuesta(acciones, encuesta))
        );
    }
    public borrarAccionItem(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO, accion: AccionesItemDTO
    ): Observable<void> {
        return this.encuestasServiceBackend.deleteAccionItem(encuesta.id, pregunta.id, item.id, accion.id);
    }

    public subirImagenItem(
        encuesta: EncuestaDTO, pregunta: PreguntaDTO, item: ItemPreguntaDTO, imageURLDTO: ImagenURLDTO
    ): Observable<PreguntaDTO> {
        return this.encuestasServiceBackend.postImagenItem(encuesta.id, pregunta.id, item.id, imageURLDTO);
    }

    private async procesarExcel(
        nombre: string, data: any, paises: PaisDTO[], investigacion_id: number
    ): Promise<Nilable<EncuestaDTO>> {
        const workbook = XLSX.read(data, {
            type: "binary"
        });

        const comboName = workbook.SheetNames[0];
        const hoja1 = workbook.Sheets[comboName];

        const filas = XLSX.utils.sheet_to_json<any>(hoja1);

        const encuesta = await this.filasAEncuesta(filas, paises);
        if (encuesta == null) {
            return null;
        }
        encuesta.nombre = nombre;
        encuesta.primeraPregunta = encuesta.preguntas.length > 0 ? encuesta.preguntas[0].id : "1";
        encuesta.investigacionId = investigacion_id;
        return await this.subirCuestionario(encuesta);
    }

    private async filasAEncuesta(
        filasPreguntas: any[], paises: PaisDTO[], filasEsMap: boolean = true
    ): Promise<EncuestaCargaDTO | null> {
        const {encuesta, errores, advertencias} = this.generarEncuestaCarga(filasPreguntas, paises, filasEsMap);

        if (encuesta.preguntas == null || encuesta.preguntas.length === 0) {
            this.notificacionService.notificarError("El combo no tiene preguntas, por favor reviselo y " +
                "vuelva a cargarlo");
            return null;
        }
        if (errores.length !== 0) {
            await this.modalService.openModalError(this.generarMensaje(advertencias, errores));
            return null;
        }

        const seguroSubir = await this.modalService.openModalDecision(
            "El Combo no contiene errores ¿Desea subirlo?<br><br>" +
            this.generarMensaje(advertencias)
        );
        if (!seguroSubir) {
            return null;
        }
        const nombreAmigable = await this.modalService.openModalInput("Ingrese un nombre amigable para el panelista", "");
        if (nombreAmigable == null || nombreAmigable === "") {
            return null;
        }
        encuesta.nombreAmigable = nombreAmigable;
        const puntaje = await this.modalService.openModalInputNumero("Ingrese el puntaje", 1);
        if (puntaje == null || puntaje <= 0) {
            return null;
        }
        encuesta.puntajeAGanar = puntaje;
        return encuesta;
    }

    private generarEncuestaCarga(
        filasPreguntas: any[], paises: PaisDTO[], filasEsMap: boolean = true
    ): EncuestaCargaDatos {
        const encuesta = new EncuestaCargaDTO();
        const preguntas = new Map<number, DatosPreguntaImportador[]>();
        let errores: string[] = [];
        let advertencias: string[] = [];

        if(filasPreguntas == undefined ||filasPreguntas === null || filasPreguntas.length === 0) {
            return {
                encuesta,
                errores: ['El archivo no tiene preguntas'],
                advertencias
            };
        }

        // Verifico si en la columna siguiente hay un 2 osea que falta una columna por lo tanto es nueva estructura
        const esNuevaEstructura = filasEsMap ? false : filasPreguntas[0][5].toString() === "2";
        this.cambiarStringANulls(filasPreguntas);
        filasPreguntas
            .filter(fila => !Array.isArray(fila) ||
                (Array.isArray(fila) && fila.length > 0 && !fila.every(elemento => elemento == null)))
            .map(fila => this.controlAntesParsearFila(fila, filasEsMap, esNuevaEstructura, errores))
            .map(fila => this.parsearFila(fila, filasEsMap, esNuevaEstructura))
            .forEach(fila => {
                if (fila.idPregunta.includes(".")) {
                    const idPadre = parseInt(fila.idPregunta.split(".")[0], 10);
                    if (!preguntas.has(idPadre)) {
                        //TODO: No tiene padre
                        return null;
                    }
                    preguntas.get(idPadre)!.push(fila);
                } else {
                    if (preguntas.has(parseInt(fila.idPregunta, 10))) {
                        errores.push(`El numero de pregunta ${parseInt(fila.idPregunta, 10)} esta repetido.`);
                    }
                    preguntas.set(parseInt(fila.idPregunta, 10), [fila]);
                }
            });
        const datosAnteriores: DatosAnteriores = {
            idsItems: [],
            idsPreguntasApuntadas: new Set(),
            paisesCodigos: paises.map(pais => pais.codigo)
        };
        datosAnteriores.idsPreguntasApuntadas.add("1");
        Array.from(preguntas.values())
            .filter(datosPregunta => datosPregunta[0].tipo != null)
            .forEach((datosPregunta, i, preguntasCompleto) => {
                const importador = this.importadoresPreguntas.find(importadorPregunta =>
                    importadorPregunta.puedeProcesar(datosPregunta[0].tipo!));
                if (importador == null) {
                    return;
                }
                importador.cargarDatosAnteriores(datosAnteriores);
                const preguntasImportador = importador.procesar(datosPregunta);
                const pasoTest = importador.testear(datosPregunta, preguntasCompleto.length === i + 1);
                if (!pasoTest) {
                    errores = [...errores, ...importador.obtenerErrores()];
                }
                advertencias = [...advertencias, ...importador.obtenerAdvertencias()];
                encuesta.preguntas.push(...preguntasImportador);
            });
        return {
            encuesta,
            errores,
            advertencias
        };
    }

    private cambiarStringANulls(filas: any[]) {
        filas.forEach((fila, index) => {
            Object.keys(fila).forEach(clave => {
                filas[index][clave] = fila[clave]?.toString().trim() === "" ? null : fila[clave];
            });
        });
    }

    private parsearFila(fila: any, filasEsMap: boolean = true, esNuevaEstructura = false): DatosPreguntaImportador {
        const obtenerNombreColumna = filasEsMap ? obtenerColumnaMap :
            (esNuevaEstructura ?  obtenerColumnaNuevoArray : obtenerColumnaArray);
        const minMax: Nilable<string> = Object.keys(fila).includes(obtenerNombreColumna(FilasExcel.MIN_MAX)) ?
            fila[obtenerNombreColumna(FilasExcel.MIN_MAX)] : null;
        let minMaxSplit: number[] = [parseInt(minMax ?? "0", 10)];
        if (minMax != null && `${minMax}`.includes(";")) {
            minMaxSplit = `${minMax}`.split(";").map(Number);
        }
        let tipoOpcion = null;
        if ((filasEsMap || esNuevaEstructura) && fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)] != null &&
            ["R", "K", "T", "B", ""].includes(fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)])) {
            tipoOpcion = fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)];
        }
        return {
            idPregunta: fila[obtenerNombreColumna(FilasExcel.PREGUNTA_ID)].toString(),
            tipo: fila[obtenerNombreColumna(FilasExcel.TIPO_PREGUNTA)] != null ?
                Constants.obtenerTipo(fila[obtenerNombreColumna(FilasExcel.TIPO_PREGUNTA)]) : null,
            texto: fila[obtenerNombreColumna(FilasExcel.TEXTO_PREGUNTA)],
            random: fila[obtenerNombreColumna(FilasExcel.ALEATORIO)] != null ?
                Boolean(fila[obtenerNombreColumna(FilasExcel.ALEATORIO)]) : null,
            referencia: fila[obtenerNombreColumna(FilasExcel.REFERENCIA)],
            item: fila[obtenerNombreColumna(FilasExcel.ITEMS)],
            preguntaSiguiente: fila[obtenerNombreColumna(FilasExcel.PREGUNTA_SIGUIENTE)] == null ?
                null : parseInt(fila[obtenerNombreColumna(FilasExcel.PREGUNTA_SIGUIENTE)], 10),
            killer: fila[obtenerNombreColumna(FilasExcel.KILLER)] != null ?
                Boolean(fila[obtenerNombreColumna(FilasExcel.KILLER)]) : null,
            condiciones: fila[obtenerNombreColumna(FilasExcel.VISIBLE_SI)]?.toString(),
            min: minMaxSplit.length > 0 ? minMaxSplit[0] : null,
            max: minMaxSplit.length > 1 ? minMaxSplit[1] : null,
            step: minMaxSplit.length > 2 ? minMaxSplit[2] : null,
            customerId: Object.keys(fila).includes(obtenerNombreColumna(FilasExcel.CUSTOMER_ID)) ?
                fila[obtenerNombreColumna(FilasExcel.CUSTOMER_ID)] : null,
            bloqueId: Object.keys(fila).includes(obtenerNombreColumna(FilasExcel.BLOQUE_ID)) ?
                fila[obtenerNombreColumna(FilasExcel.BLOQUE_ID)] : null,
            tipoOpcion
        };
    }

    private generarMensaje(
        advertencias: string[] = [], errores: string[] = []
    ): string {
        let mensaje = "";
        if (errores.length !== 0) {
            mensaje += "El archivo Excel tiene los siguientes problemas:<br><br>";
        }
        if (advertencias.length !== 0) {
            mensaje += `Advertencias:<br>${advertencias.join("<br>")}<br><br>`;
        }
        if (errores.length !== 0) {
            mensaje += `Combo:<br>${errores.join("<br>")}<br><br>`;
        }
        return mensaje;
    }

    private asignarIdRelativo(encuesta: EncuestaDTO) {
        let i = 1;
        encuesta.preguntas!.forEach(pregunta => {
            if (pregunta.padre != null) {
                const preguntaDTO = this.getPreguntaById(encuesta, pregunta.padre);

                if (preguntaDTO.preguntas == null) {
                    preguntaDTO.preguntas = [];
                }

                preguntaDTO.preguntas.push(pregunta);
                pregunta.idRelativo = `${preguntaDTO.idRelativo}.${preguntaDTO.preguntas.length}`;

                let k = 1;
                preguntaDTO.items.forEach(item => {
                    item.idRelativo = `${preguntaDTO.idRelativo}.${preguntaDTO.preguntas!.length + k}`;
                    k++;
                });

                return;
            }

            pregunta.idRelativo = `${i}`;

            let j = 1;
            pregunta.items.forEach(item => {
                item.idRelativo = `${i}.${j}`;
                j++;
            });

            i++;
        });
    }

    private cargarAccionesEnEncuesta(acciones: AccionesItemDTO[], encuesta: EncuestaDTO): void {
        acciones.forEach(accion => {
            const pregunta = encuesta.preguntas?.find(preguntaDTO =>
                preguntaDTO.items.some(itemDTO => itemDTO.id === accion.id));
            const item = pregunta?.items.find(itemDTO => itemDTO.id === accion.item);
            if (item != null) {
                item.acciones.push(accion);
            }
        });
    }

    private controlAntesParsearFila(
        fila: any, filasEsMap: boolean = true, esNuevaEstructura = false, errores: string[]
    ): any {
        const obtenerNombreColumna = filasEsMap ? obtenerColumnaMap :
            (esNuevaEstructura ?  obtenerColumnaNuevoArray : obtenerColumnaArray);
        const columnas = obtenerColumnas();
        columnas.forEach(columna => {
            if (fila[obtenerNombreColumna(columna)] != null && fila[obtenerNombreColumna(columna)].toString().includes("#REF")) {
                errores.push(`La fila ${fila[obtenerNombreColumna(FilasExcel.PREGUNTA_ID)]} tiene un error en la formula`);
            }
        });
        if (fila[obtenerNombreColumna(FilasExcel.PREGUNTA_ID)] > 1000) {
            errores.push(`En la fila ${fila.__rowNum__} y columna ${obtenerNombreColumna(FilasExcel.PREGUNTA_ID)} tiene un error de formato (',' en vez de '.' o formato fecha)`);
        }
        if (fila[obtenerNombreColumna(FilasExcel.PREGUNTA_SIGUIENTE)] > 1000) {
            errores.push(`En la fila ${fila.__rowNum__} y columna ${obtenerNombreColumna(FilasExcel.PREGUNTA_SIGUIENTE)} tiene un error de formato (',' en vez de '.' o formato fecha)`);
        }
        if (fila[obtenerNombreColumna(FilasExcel.MIN_MAX)] != null &&
            fila[obtenerNombreColumna(FilasExcel.MIN_MAX)].includes(":")
        ) {
            errores.push(`En la columna de Min/Max de la fila ${fila[obtenerNombreColumna(FilasExcel.PREGUNTA_ID)]} hay un : y no un ;`);
        }
        if (fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)] != null) {
            if (filasEsMap) {
                if (fila[obtenerNombreColumna(FilasExcel.ALEATORIO)] != null) {
                    errores.push(`La columna ${obtenerNombreColumna(FilasExcel.ALEATORIO)} no puede existir con la columna ${obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)}`);
                }
                if (fila[obtenerNombreColumna(FilasExcel.KILLER)] != null) {
                    errores.push(`La columna ${obtenerNombreColumna(FilasExcel.KILLER)} no puede existir con la columna ${obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)}`);
                }
                if (!esNuevaEstructura && !["R", "K", "T", "B", ""].includes(fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)])) {
                    errores.push(`En la pregunta ${fila[FilasExcel.PREGUNTA_ID]} columna ${obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)} solo pueden estar las letras 'R', 'K', 'T' o 'B'`);
                }
            }
            if (!filasEsMap) {
                if (esNuevaEstructura && ["R", "K", "T", "B", ""].includes(fila[obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)])) {
                    if (fila[obtenerNombreColumna(FilasExcel.ALEATORIO)] != null) {
                        errores.push(`La columna ${obtenerNombreColumna(FilasExcel.ALEATORIO)} no puede existir con la columna ${obtenerNombreColumna(FilasExcel.TIPO_OPCIONES)}`);
                    }
                }
            }
        }
        if (fila[obtenerNombreColumna(FilasExcel.CUSTOMER_ID)] != null) {
            if (fila[obtenerNombreColumna(FilasExcel.CUSTOMER_ID)].length > 20) {
                errores.push(`La columna ##Customer ID en la pregunta ${fila[obtenerNombreColumna(FilasExcel.PREGUNTA_ID)]} no puede tener mas de 20 caracteres.`);
            }
        }
        return fila;
    }

    private tienePostAcciones(encuestaCarga: EncuestaCargaDTO): boolean {
        return encuestaCarga.preguntas!.some(pregunta => pregunta.tipo === PreguntasTipoEnum.GRAPHICAL_CHOICE);
    }

    private async cargarFotosGraphicalChoice(
        encuestaCarga: EncuestaCargaDTO, encuesta: EncuestaDTO, preguntas: PreguntaDTO[]
    ): Promise<void> {
        const preguntasGraphicalChoice = preguntas.filter(x => x.tipo === PreguntasTipoEnum.GRAPHICAL_CHOICE);
        await Promise.all(
            preguntasGraphicalChoice.map(async pregunta => {
                const preguntaCarga = encuestaCarga.preguntas!.find(preguntaCargaDTO =>
                    preguntaCargaDTO.tipo === PreguntasTipoEnum.GRAPHICAL_CHOICE &&
                    preguntaCargaDTO.texto === pregunta.texto);
                await Promise.all(
                    preguntaCarga!.items.map(async itemCarga => {
                        const item = pregunta.items.find(itemDTO => itemDTO.texto === itemCarga.texto)!;
                        const imagenURLDTO = new ImagenURLDTO();
                        imagenURLDTO.imagenURl = itemCarga!.url!;
                        await firstValueFrom(
                            this.subirImagenItem(encuesta, pregunta, item, imagenURLDTO)
                        );
                    })
                );
            })
        );
    }
}
